import { jwtDecode } from "jwt-decode";

const FULL_ACCESS_ROLES = new Set(["CEO", "Exclusive Admin"]);
const SUPER_ADMIN_ONLY_ROLES = new Set(["Admin App"]);
const LIMITED_ACCESS_ROLES = new Set([
  "Manager",
  "Manager สำนักงานใหญ่",
  "Branch Admin",
  "Owner",
  "Owner แฟรนไชส์",
  "Branch Manager",
  "Manager สาขา",
]);

export const ADMIN_ROLE_TITLES = new Set([
  "CEO",
  "Exclusive Admin",
  "Admin App",
  "Manager",
  "Manager สำนักงานใหญ่",
  "Branch Admin",
  "Admin สาขา",
  "Owner",
  "Owner แฟรนไชส์",
  "Branch Manager",
  "Manager สาขา",
]);

// This role is intentionally accepted for login but cannot be assigned from
// any Admin screen. Set User.adminRole directly in the database instead.
export const DATABASE_ONLY_ADMIN_ROLE_TITLES = new Set(["Admin App"]);

export const isSuperAdminOnlyRole = (role) =>
  SUPER_ADMIN_ONLY_ROLES.has(role);

const BRANCH_SCOPED_ROLES = new Set(["Owner", "Branch Manager"]);
export const BRANCH_SCOPED_USER_ROLE_TITLES = new Set([
  "Owner",
  "Branch Manager",
  "Cashier",
  "Scale",
]);

const LIMITED_PATHS = [
  /^\/$/,
  /^\/products(?:\/|$)/,
  /^\/step-price(?:\/|$)/,
  /^\/categories(?:\/|$)/,
  /^\/branches\/?$/,
  /^\/branches\/pricing(?:\/|$)/,
  /^\/users(?:\/|$)/,
  /^\/groups(?:\/|$)/,
];

export const getAccessTokenPayload = () => {
  const token = localStorage.getItem("accessToken");
  if (!token) return null;

  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

export const getAdminRole = (payload = getAccessTokenPayload()) => {
  const role = payload?.role;
  if (typeof role !== "string") return null;

  const aliases = {
    "Manager สำนักงานใหญ่": "Manager",
    "Owner แฟรนไชส์": "Owner",
    "Manager สาขา": "Branch Manager",
    "Admin สาขา": "Branch Admin",
  };
  return aliases[role] || role;
};

export const isFullAdminRole = (role) => FULL_ACCESS_ROLES.has(role);

export const isBranchScopedAdminRole = (role) =>
  BRANCH_SCOPED_ROLES.has(getAdminRole({ role }));

export const getAdminBranchId = (payload = getAccessTokenPayload()) => {
  const branchId = Number(payload?.branchId);
  return Number.isInteger(branchId) && branchId > 0 ? branchId : null;
};

export const isSupportedAdminRole = (role) =>
  FULL_ACCESS_ROLES.has(role) ||
  SUPER_ADMIN_ONLY_ROLES.has(role) ||
  LIMITED_ACCESS_ROLES.has(role);

export const canAccessAdminPath = (role, pathname) => {
  if (/^\/super-admin(?:\/|$)/.test(pathname)) {
    return isSuperAdminOnlyRole(role);
  }
  if (isFullAdminRole(role)) return true;
  if (isSuperAdminOnlyRole(role)) return false;
  if (!LIMITED_ACCESS_ROLES.has(role)) return false;
  if (
    isBranchScopedAdminRole(role) &&
    /^(?:\/$|\/products(?:\/|$)|\/step-price(?:\/|$)|\/categories(?:\/|$)|\/groups(?:\/|$))/.test(
      pathname,
    )
  ) {
    return false;
  }
  return LIMITED_PATHS.some((pattern) => pattern.test(pathname));
};

const LIMITED_SEGMENTS = new Set([
  "products",
  "categories",
  "branches",
  "users",
  "groups",
]);

export const getAdminNavigation = (navigation, role) => {
  if (isFullAdminRole(role)) {
    return navigation.filter((item) => item.segment !== "super-admin");
  }
  if (isSuperAdminOnlyRole(role)) {
    return navigation.filter((item) => item.segment === "super-admin");
  }
  if (!LIMITED_ACCESS_ROLES.has(role)) return [];

  const filtered = [];
  let pendingHeader = null;

  navigation.forEach((item) => {
    if (item.kind === "header") {
      pendingHeader = item;
      return;
    }

    if (!LIMITED_SEGMENTS.has(item.segment)) return;
    if (
      isBranchScopedAdminRole(role) &&
      ["products", "categories", "groups"].includes(item.segment)
    ) {
      return;
    }

    if (pendingHeader) {
      filtered.push(pendingHeader);
      pendingHeader = null;
    }
    filtered.push(item);
  });

  return filtered;
};
